/**
 * Settings form component
 * Upload limits and Haravan integration configuration
 */
import React, { useState } from 'react';
import { Save, Loader2 } from 'lucide-react';

interface SettingsFormProps {
  settings: {
    upload_limits: {
      per_ip_hourly: number;
      per_ip_daily: number;
      per_ip_monthly: number;
      per_session: number;
      global_daily: number;
    };
    haravan_integration: {
      enabled: boolean;
      store_url: string;
      phone_case_product_path: string;
      coffee_cup_product_path: string;
      widget_button_text: string;
      widget_button_color: string;
      redirect_url_template: string;
    };
  };
  onSave: (settings: any) => void;
  saving: boolean;
}

export default function SettingsForm({ settings, onSave, saving }: SettingsFormProps) {
  const [formData, setFormData] = useState(settings);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const updateUploadLimit = (key: string, value: number) => {
    setFormData({
      ...formData,
      upload_limits: {
        ...formData.upload_limits,
        [key]: value
      }
    });
  };

  const updateHaravan = (key: string, value: any) => {
    setFormData({
      ...formData,
      haravan_integration: {
        ...formData.haravan_integration,
        [key]: value
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Upload Limits */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Giới hạn tải lên</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mỗi IP - Theo giờ
            </label>
            <input
              type="number"
              value={formData.upload_limits.per_ip_hourly}
              onChange={(e) => updateUploadLimit('per_ip_hourly', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              min="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mỗi IP - Theo ngày
            </label>
            <input
              type="number"
              value={formData.upload_limits.per_ip_daily}
              onChange={(e) => updateUploadLimit('per_ip_daily', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              min="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mỗi IP - Theo tháng
            </label>
            <input
              type="number"
              value={formData.upload_limits.per_ip_monthly}
              onChange={(e) => updateUploadLimit('per_ip_monthly', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              min="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mỗi phiên
            </label>
            <input
              type="number"
              value={formData.upload_limits.per_session}
              onChange={(e) => updateUploadLimit('per_session', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              min="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Giới hạn toàn cục theo ngày
            </label>
            <input
              type="number"
              value={formData.upload_limits.global_daily}
              onChange={(e) => updateUploadLimit('global_daily', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              min="1"
            />
          </div>
        </div>
      </div>

      {/* Haravan Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Tích hợp Haravan</h2>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.haravan_integration.enabled}
              onChange={(e) => updateHaravan('enabled', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Bật tích hợp</span>
          </label>
        </div>

        {formData.haravan_integration.enabled && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL cửa hàng
              </label>
              <input
                type="url"
                value={formData.haravan_integration.store_url}
                onChange={(e) => updateHaravan('store_url', e.target.value)}
                placeholder="https://yourstore.myharavan.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Đường dẫn sản phẩm ốp lưng
                </label>
                <input
                  type="text"
                  value={formData.haravan_integration.phone_case_product_path}
                  onChange={(e) => updateHaravan('phone_case_product_path', e.target.value)}
                  placeholder="/products/custom-phone-case"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Đường dẫn sản phẩm cốc cà phê
                </label>
                <input
                  type="text"
                  value={formData.haravan_integration.coffee_cup_product_path}
                  onChange={(e) => updateHaravan('coffee_cup_product_path', e.target.value)}
                  placeholder="/products/custom-coffee-cup"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Văn bản nút widget
              </label>
              <input
                type="text"
                value={formData.haravan_integration.widget_button_text}
                onChange={(e) => updateHaravan('widget_button_text', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Màu nút widget
              </label>
              <input
                type="color"
                value={formData.haravan_integration.widget_button_color}
                onChange={(e) => updateHaravan('widget_button_color', e.target.value)}
                className="w-20 h-10 border border-gray-300 rounded-lg cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mẫu URL chuyển hướng
              </label>
              <input
                type="text"
                value={formData.haravan_integration.redirect_url_template}
                onChange={(e) => updateHaravan('redirect_url_template', e.target.value)}
                placeholder="https://yourstore.myharavan.com/cart/add?id={productId}&properties[DesignID]={submissionId}"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Sử dụng {'{productId}'} và {'{submissionId}'} làm placeholder
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Đang lưu...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Lưu cài đặt
            </>
          )}
        </button>
      </div>
    </form>
  );
}

